import React, { useState, useMemo } from 'react';
import { useData } from '../App';
import { UserProfile, Neighborhood } from '../types';
import { 
  Building, Users, Car, Heart, AlertTriangle, 
  ArrowRightLeft, X, Check, ArrowUpDown, Filter, 
  MapPin, Star, ThumbsUp, Home, Quote, MessageCircle, Sparkles, SearchX
} from 'lucide-react';

interface NeighborhoodsProps {
  userProfile: UserProfile;
}

const Neighborhoods: React.FC<NeighborhoodsProps> = ({ userProfile }) => {
  const { neighborhoods } = useData();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [sortBy, setSortBy] = useState<'recommended' | 'price' | 'commute' | 'nri'>('recommended');
  const [filterMarket, setFilterMarket] = useState<string>('all');

  // --- HELPER LOGIC ---

  // Determine user preferences
  const isRent = userProfile?.budget.toLowerCase().includes('rent') ?? true;
  const workLocation = userProfile?.workLocation || 'unsure';
  const isWfh = workLocation === 'wfh'; // Only true WFH triggers the N/A message
  
  // Map user work location to data keys
  const getCommuteKey = (location: string) => {
    switch(location) {
      case 'hinjewadi': return 'hinjewadi';
      case 'kharadi': return 'eon_it_park';
      case 'magarpatta': return 'magarpatta';
      default: return 'airport'; // Fallback
    }
  };
  const commuteKey = getCommuteKey(workLocation);
  
  // Helper for Work Location Label
  const getWorkLocationLabel = () => {
    switch(workLocation) {
      case 'hinjewadi': return 'Hinjewadi IT Park';
      case 'kharadi': return 'EON IT Park';
      case 'magarpatta': return 'Magarpatta City';
      case 'wfh': return 'Home Office';
      default: return 'City Center';
    }
  };

  // Color Coding Logic
  const getCommuteColor = (minutes: number) => {
    if (minutes < 20) return { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600' };
    if (minutes <= 35) return { text: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600' };
    if (minutes <= 50) return { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600' };
    return { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600' };
  };

  // Recommendation Engine
  const getRecommendationScore = (n: Neighborhood) => {
    let score = n.nriCommunityScore * 10; // Base score (0-100)
    
    // Explicit Mappings from requirements
    const recommendedIds: Record<string, string[]> = {
      'hinjewadi': ['baner', 'balewadi', 'wakad', 'pimple_saudagar'],
      'kharadi': ['kharadi', 'viman_nagar', 'amanora'],
      'magarpatta': ['amanora', 'hadapsar', 'kharadi', 'magarpatta']
    };

    if (workLocation && recommendedIds[workLocation]?.includes(n.id)) {
      score += 50; // Huge boost for direct recommendations
    }

    // Commute penalty (if not WFH)
    if (!isWfh && workLocation !== 'unsure' && n.commuteMinutes[commuteKey]) {
      const time = n.commuteMinutes[commuteKey] || 0;
      if (time <= 20) score += 20;
      else if (time <= 40) score += 10;
      else if (time > 60) score -= 20;
    }

    return score;
  };

  // --- SORTING & FILTERING ---

  const processedData = useMemo(() => {
    let data = [...neighborhoods];

    // Filter
    if (filterMarket !== 'all') {
      data = data.filter(n => n.microMarket === filterMarket);
    }

    // Sort
    data.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          if (isRent) {
            return ((a.propertyPriceRange.rent3bhk.min + a.propertyPriceRange.rent3bhk.max) / 2) - 
                   ((b.propertyPriceRange.rent3bhk.min + b.propertyPriceRange.rent3bhk.max) / 2);
          } else {
             return ((a.propertyPriceRange.buy3bhk.min + a.propertyPriceRange.buy3bhk.max) / 2) - 
                    ((b.propertyPriceRange.buy3bhk.min + b.propertyPriceRange.buy3bhk.max) / 2);
          }
        case 'commute':
          if (isWfh) return 0;
          return (a.commuteMinutes[commuteKey] || 999) - (b.commuteMinutes[commuteKey] || 999);
        case 'nri':
          return b.nriCommunityScore - a.nriCommunityScore;
        case 'recommended':
        default:
          return getRecommendationScore(b) - getRecommendationScore(a);
      }
    });

    return data;
  }, [filterMarket, sortBy, userProfile, isRent, isWfh, commuteKey, workLocation, neighborhoods]);

  // --- HANDLERS ---

  const toggleCompare = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(pid => pid !== id));
    } else {
      if (selectedIds.length < 3) {
        setSelectedIds([...selectedIds, id]);
      } else {
        alert("You can compare up to 3 neighborhoods at a time.");
      }
    }
  };

  // --- COMPARISON VIEW ---

  const renderComparisonModal = () => {
    const items = neighborhoods.filter(n => selectedIds.includes(n.id));

    return (
      <div 
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="comparison-title"
      >
        <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 id="comparison-title" className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ArrowRightLeft size={20} className="text-indigo-600" /> Compare Neighborhoods
            </h3>
            <button 
              onClick={() => setShowComparison(false)} 
              className="p-2 hover:bg-slate-200 rounded-full transition"
              aria-label="Close comparison"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>
          
          <div className="overflow-auto p-6 custom-scrollbar">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-3 border-b-2 border-slate-100 min-w-[150px]">Feature</th>
                  {items.map(item => (
                    <th key={item.id} className="p-3 border-b-2 border-slate-100 min-w-[200px]">
                      <div className="text-lg font-bold text-slate-800">{item.name}</div>
                      <div className="text-xs font-normal text-slate-500 capitalize">{item.microMarket.replace('_', ' ')}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                <tr>
                  <td className="p-3 font-semibold text-slate-600">NRI Community Score</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${item.nriCommunityScore >= 8 ? 'text-emerald-600' : 'text-indigo-600'}`}>
                          {item.nriCommunityScore}/10
                        </span>
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.nriCommunityScore >= 8 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{width: `${item.nriCommunityScore * 10}%`}}></div>
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600">Commute to {getWorkLocationLabel()}</td>
                  {items.map(item => {
                    const mins = item.commuteMinutes[commuteKey] || 0;
                    const colors = getCommuteColor(mins);
                    
                    if (isWfh) {
                      return <td key={item.id} className="p-3 text-slate-400 font-medium">N/A - WFH</td>;
                    }

                    return (
                      <td key={item.id} className="p-3">
                         <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${colors.bg} ${colors.border}`}>
                            <Car size={14} className={colors.icon} />
                            <span className={`font-bold ${colors.text}`}>{mins} mins</span>
                         </div>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50/50">Avg 3BHK Rent</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 bg-slate-50/50 font-medium">
                      ₹{(item.propertyPriceRange.rent3bhk.min/1000).toFixed(0)}k - {(item.propertyPriceRange.rent3bhk.max/1000).toFixed(0)}k
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600">Avg 3BHK Buy</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 font-medium">
                      ₹{item.propertyPriceRange.buy3bhk.min} - {item.propertyPriceRange.buy3bhk.max} Cr
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 align-top pt-4">Community Verdict</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top pt-4">
                      {item.chatQuote ? (
                        <div className="text-xs text-slate-600 italic border-l-2 border-indigo-200 pl-2">
                           "{item.chatQuote}"
                        </div>
                      ) : <span className="text-xs text-slate-400">No specific quotes available</span>}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 align-top pt-4">Top Pros</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top pt-4">
                      <ul className="space-y-1">
                        {item.pros.slice(0, 3).map((p, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <Check size={12} className="mt-0.5 text-emerald-500 flex-shrink-0" /> {p}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 align-top">Cons</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top">
                      <ul className="space-y-1">
                        {item.cons.slice(0, 2).map((c, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <AlertTriangle size={12} className="mt-0.5 text-amber-500 flex-shrink-0" /> {c}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600">Nearby Schools</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-slate-100 text-xs font-medium text-slate-700">
                        {item.nearbySchools.length} top schools
                      </span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto pb-24 md:pb-6">
      {/* HEADER & FILTERS */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            Neighborhood Finder
            <span className="text-xs font-medium px-2 py-1 bg-amber-100 text-amber-700 rounded-full">📍 Pune</span>
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isWfh 
              ? "Since you're working from home, we've prioritized community and amenities." 
              : <span>Optimized for commute to <span className="font-semibold text-slate-700">{getWorkLocationLabel()}</span>.</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {/* Market Filter */}
          <div className="relative">
             <div className="absolute left-3 top-2.5 pointer-events-none text-slate-400">
               <Filter size={14} />
             </div>
             <select 
               className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer hover:border-slate-300 transition shadow-sm"
               value={filterMarket}
               onChange={(e) => setFilterMarket(e.target.value)}
               aria-label="Filter by market"
             >
               <option value="all">All Zones</option>
               <option value="pune_east">Pune East (IT)</option>
               <option value="pune_west">Pune West</option>
               <option value="pune_south">Pune South</option>
               <option value="pcmc_it">PCMC (Hinjewadi)</option>
             </select>
          </div>

          {/* Sort Control */}
          <div className="relative">
             <div className="absolute left-3 top-2.5 pointer-events-none text-slate-400">
               <ArrowUpDown size={14} />
             </div>
             <select 
               className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer hover:border-slate-300 transition shadow-sm"
               value={sortBy}
               onChange={(e) => setSortBy(e.target.value as any)}
               aria-label="Sort neighborhoods"
             >
               <option value="recommended">Recommended</option>
               <option value="price">Lowest Price</option>
               <option value="commute">Shortest Commute</option>
               <option value="nri">NRI Score</option>
             </select>
          </div>
        </div>
      </div>

      {/* EMPTY STATE */}
      {processedData.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-300 animate-in fade-in zoom-in-95">
          <div className="bg-slate-50 p-4 rounded-full inline-block mb-4">
            <SearchX size={48} className="text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-700 mb-2">No neighborhoods found</h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto">
            We couldn't find any neighborhoods matching your current filters in {filterMarket !== 'all' ? filterMarket.replace('_', ' ') : 'Pune'}.
          </p>
          <button 
            onClick={() => {setFilterMarket('all'); setSortBy('recommended');}}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-200"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        /* GRID */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {processedData.map((area, index) => {
            const isSelected = selectedIds.includes(area.id);
            const isRecommended = getRecommendationScore(area) > 80;
            const isPopular = area.mentionedInChat > 20;
            const isHighlyRated = area.chatSentiment === 'very_positive';
            
            // Commute Calculations
            const minutes = area.commuteMinutes[commuteKey] || 0;
            const commuteColors = getCommuteColor(minutes);

            return (
              <div 
                key={area.id} 
                className={`bg-white rounded-xl border transition-all duration-200 flex flex-col h-full group
                  ${isSelected 
                    ? 'border-indigo-500 ring-2 ring-indigo-500/20 shadow-lg scale-[1.01]' 
                    : 'border-slate-200 hover:border-indigo-200 hover:shadow-lg hover:-translate-y-1'
                  }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Card Header */}
                <div className="p-5 flex-1">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-800">{area.name}</h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {isRecommended && (
                           <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide flex items-center gap-1">
                             <ThumbsUp size={10} /> Top Pick
                           </span>
                        )}
                        {isPopular && (
                           <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide flex items-center gap-1">
                             <MessageCircle size={10} /> Popular
                           </span>
                        )}
                        {isHighlyRated && (
                           <span className="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide flex items-center gap-1">
                             <Heart size={10} /> Loved
                           </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 font-medium capitalize mt-1.5">
                        {area.microMarket.replace('_', ' ')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                        <Star size={12} className={area.nriCommunityScore >= 8 ? 'text-emerald-500 fill-emerald-500' : 'text-amber-500 fill-amber-500'} />
                        <span className="text-xs font-bold text-slate-700">{area.nriCommunityScore}/10</span>
                      </div>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* Price */}
                    <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                        {isRent ? 'Est. Rent (3BHK)' : 'Est. Price (3BHK)'}
                      </p>
                      <p className="text-sm font-bold text-slate-700">
                        {isRent 
                          ? `₹${(area.propertyPriceRange.rent3bhk.min/1000).toFixed(0)}k - ${(area.propertyPriceRange.rent3bhk.max/1000).toFixed(0)}k`
                          : `₹${area.propertyPriceRange.buy3bhk.min} - ${area.propertyPriceRange.buy3bhk.max} Cr`
                        }
                      </p>
                    </div>
                    
                    {/* Dynamic Commute Block */}
                    <div className={`p-2.5 rounded-lg border transition-colors ${isWfh ? 'bg-slate-50 border-slate-100' : `${commuteColors.bg} ${commuteColors.border}`}`}>
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-1 truncate">
                        Commute to {isWfh ? 'Office' : (workLocation === 'hinjewadi' ? 'Hinjewadi' : (workLocation === 'kharadi' ? 'EON Park' : 'Work'))}
                      </p>
                      
                      {isWfh ? (
                        <div className="flex items-center gap-1.5">
                           <Home size={14} className="text-slate-400" />
                           <span className="text-sm font-bold text-slate-500">N/A - WFH</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Car size={14} className={commuteColors.icon} />
                          <span className={`text-sm font-bold ${commuteColors.text}`}>
                            {minutes} min
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pros */}
                  <div className="space-y-2 mb-4">
                    {area.pros.slice(0, 3).map((pro, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                        <Check size={12} className="mt-0.5 text-emerald-500 flex-shrink-0" />
                        <span>{pro}</span>
                      </div>
                    ))}
                  </div>

                  {/* Community Quote */}
                  {area.chatQuote && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 relative group-hover:border-indigo-100 transition-colors">
                        <Quote size={14} className="text-indigo-300 absolute -top-2 -left-1 bg-white rounded-full p-0.5 border border-indigo-100" />
                        <p className="text-xs text-slate-600 italic leading-relaxed">"{area.chatQuote}"</p>
                        <div className="mt-1 flex items-center justify-end gap-1">
                           <Users size={10} className="text-indigo-400" />
                           <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wide">Community Verified</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-xl flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group/label select-none">
                     <div className={`w-5 h-5 rounded border flex items-center justify-center transition
                       ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300 group-hover/label:border-indigo-400'}`}>
                       {isSelected && <Check size={14} className="text-white" />}
                     </div>
                     <input 
                       type="checkbox" 
                       className="hidden" 
                       checked={isSelected}
                       onChange={() => toggleCompare(area.id)}
                       aria-label={`Compare ${area.name}`}
                     />
                     <span className={`text-sm font-medium transition ${isSelected ? 'text-indigo-700' : 'text-slate-600 group-hover/label:text-indigo-600'}`}>
                       Compare
                     </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* COMPARISON FLOATING BAR */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl z-40 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-300">
          <span className="font-medium text-sm">{selectedIds.length} selected</span>
          <div className="h-4 w-px bg-slate-700"></div>
          <button 
            onClick={() => setShowComparison(true)}
            className="flex items-center gap-2 font-bold text-sm text-indigo-300 hover:text-white transition"
          >
            Compare Now <ArrowRightLeft size={16} />
          </button>
          <button 
            onClick={() => setSelectedIds([])} 
            className="ml-2 hover:bg-slate-800 p-1 rounded-full transition"
            aria-label="Clear selection"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {showComparison && renderComparisonModal()}
    </div>
  );
};

export default Neighborhoods;